import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';

const useOdTaskTemplatesApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const merged = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : (lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 });
    lastValidQueryParams.current = merged;
    setFetching(true);
    api.get(`/v1/offboarding-task-templates?${new URLSearchParams(merged)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const remove = useCallback(async (id) => {
    try { await api.delete(`/v1/offboarding-task-templates/${id}`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'name',
      label: t('offboardingTaskTemplate.name'),
      options: {
        filter: true, sort: true,
        filterType: 'textField',
        filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'exitTypes',
      label: t('offboardingTaskTemplate.exitTypes'),
      options: {
        filter: true, sort: false,
        filterType: 'textField',
        filterOptions: { defaultOp: 'in' },
        customBodyRender: (v) => v ? v : '–',
      },
    },
    {
      name: 'isDefault',
      label: t('offboardingTaskTemplate.isDefault'),
      options: {
        filter: true, sort: true,
        customBodyRender: (v) => v ? t('common.yes') : t('common.no'),
      },
    },
    numericFilterColumn('itemCount', t('offboardingTaskTemplate.itemCount'), { decimals: 0 }),
  ], [t]);

  return { fetching, data, pagination, columns, refresh, remove };
};

export default useOdTaskTemplatesApi;
